import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Globe,
  Loader2,
  Edit3,
  Share2,
  Eye,
  EyeOff,
  Check,
  X as XIcon,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ShareProfileModal from "@/components/ShareProfileModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_LABELS = {
  streaks: "Streaks",
  badges: "Badges",
  coach: "Pinned AI Coach Plan",
  workout: "Pinned Workout",
  socials: "Social Handles",
  member_since: "Member Since",
  stickers: "Pinned Stickers",
};

export default function PublicProfileSettings() {
  const { user, refreshUser } = useAuth();
  const [shareOpen, setShareOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [handleInput, setHandleInput] = useState("");
  const [checking, setChecking] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [renameBusy, setRenameBusy] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);

  const handle = user?.handle;
  const enabled = !!user?.public_profile_enabled;
  const visibility = user?.public_visibility || {
    streaks: true,
    badges: true,
    coach: true,
    workout: true,
    socials: true,
    member_since: true,
    stickers: true,
  };

  // 🪦 Rename/Handle Cards retired Feb 2026 — kept for display continuity only
  const handleSet = !!user?.handle_set;

  // Debounced availability check
  useEffect(() => {
    if (!handleInput || handleInput === handle) {
      setAvailability(null);
      return;
    }
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/profile/handle/available`, {
          params: { handle: handleInput },
        });
        setAvailability(data);
      } catch {
        setAvailability({ available: false, reason: "Check failed." });
      } finally {
        setChecking(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [handleInput, handle]);

  const toggleEnabled = useCallback(async () => {
    setToggleBusy(true);
    try {
      await axios.post(`${API}/profile/visibility`, { enabled: !enabled });
      toast.success(
        enabled
          ? "Your profile is now private."
          : "🌐 Your public profile is live! Copy your link and share."
      );
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed.");
    } finally {
      setToggleBusy(false);
    }
  }, [enabled, refreshUser]);

  const toggleSection = useCallback(
    async (key) => {
      try {
        await axios.post(`${API}/profile/visibility`, {
          sections: { [key]: !visibility[key] },
        });
        refreshUser?.();
      } catch (e) {
        toast.error("Couldn't update visibility.");
      }
    },
    [visibility, refreshUser]
  );

  const submitRename = async () => {
    if (!availability?.available) return;
    if (handleSet && !window.confirm(`Change your handle @${handle} → @${availability.handle}?`)) return;
    setRenameBusy(true);
    try {
      const { data } = await axios.post(`${API}/profile/handle`, { handle: availability.handle });
      toast.success(`🌐 Handle updated → @${data.handle}`);
      setRenaming(false);
      setHandleInput("");
      setAvailability(null);
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed.");
    } finally {
      setRenameBusy(false);
    }
  };

  if (!handle) {
    return null;
  }

  const publicUrl = `${window.location.origin}/u/${handle}`;

  return (
    <section
      data-testid="public-profile-settings"
      className="mt-8 p-5 border border-zinc-800 bg-[#0F0F0F]"
    >
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
        Public Profile
      </div>
      <h3 className="font-display text-2xl text-white mb-3 flex items-center gap-2">
        <Globe size={18} className="text-[#D4AF37]" />
        Share Your Journey
      </h3>

      {/* Handle */}
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
          Your Handle
        </div>
        {!renaming ? (
          <div className="flex items-center gap-2 flex-wrap">
            <code
              data-testid="my-handle"
              className="text-[#D4AF37] text-lg font-mono bg-[#0A0A0A] border border-zinc-800 px-3 py-1.5"
            >
              @{handle}
            </code>
            <button
              data-testid="handle-rename-btn"
              onClick={() => {
                setRenaming(true);
                setHandleInput(handle);
              }}
              className="btn-omni btn-outline-gold text-xs"
            >
              <Edit3 size={12} /> Rename
            </button>
            <span className="text-[10px] uppercase tracking-widest text-neutral-600">
              {handleSet ? "Free to rename ✨" : "First rename free 🎁"}
            </span>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[#D4AF37] font-mono">@</span>
              <input
                data-testid="handle-input"
                value={handleInput}
                onChange={(e) => setHandleInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                placeholder="your-handle"
                maxLength={20}
                className="input-omni flex-1 font-mono"
              />
              <button
                data-testid="handle-rename-save"
                onClick={submitRename}
                disabled={renameBusy || !availability?.available}
                className="btn-omni btn-red text-xs disabled:opacity-40"
              >
                {renameBusy ? <Loader2 className="animate-spin" size={12} /> : <Check size={12} />}
                Save
              </button>
              <button
                data-testid="handle-rename-cancel"
                onClick={() => {
                  setRenaming(false);
                  setHandleInput("");
                  setAvailability(null);
                }}
                className="btn-omni btn-outline-gold text-xs"
              >
                <XIcon size={12} />
              </button>
            </div>
            {handleInput && handleInput !== handle && (
              <div className="text-xs">
                {checking ? (
                  <span className="text-neutral-500 inline-flex items-center gap-1">
                    <Loader2 className="animate-spin" size={10} /> Checking…
                  </span>
                ) : availability?.available ? (
                  <span className="text-emerald-400">
                    ✓ @{availability.handle} is available
                  </span>
                ) : availability ? (
                  <span className="text-[#DC2626]">✗ {availability.reason}</span>
                ) : null}
              </div>
            )}
            <p className="text-[10px] uppercase tracking-widest text-neutral-600">
              3-20 chars · lowercase letters/digits/dashes · starts with a letter
            </p>
          </div>
        )}
      </div>

      {/* Master switch */}
      <div className="flex items-center justify-between gap-3 p-3 bg-[#0A0A0A] border border-zinc-800 mb-4">
        <div className="flex-1">
          <div className="font-display text-base text-white flex items-center gap-2">
            {enabled ? <Eye size={14} className="text-emerald-400" /> : <EyeOff size={14} className="text-neutral-500" />}
            {enabled ? "Profile is Public" : "Profile is Private"}
          </div>
          <div className="text-xs text-neutral-500 mt-0.5">
            {enabled ? (
              <>
                Live at{" "}
                <a
                  href={publicUrl}
                  data-testid="public-url-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#D4AF37] hover:underline"
                >
                  {publicUrl}
                </a>
              </>
            ) : (
              "Enable to share your AI plan, workout, streaks & badges 🐦‍🔥"
            )}
          </div>
        </div>
        <button
          data-testid="public-toggle-btn"
          onClick={toggleEnabled}
          disabled={toggleBusy}
          className={`btn-omni ${enabled ? "btn-outline-gold" : "btn-red"} disabled:opacity-50`}
        >
          {toggleBusy ? <Loader2 className="animate-spin" size={14} /> : enabled ? <EyeOff size={14} /> : <Eye size={14} />}
          {enabled ? "Make Private" : "Go Public"}
        </button>
      </div>

      {/* Per-section toggles + share button */}
      {enabled && (
        <>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
            What's visible on your public profile
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mb-4">
            {Object.entries(SECTION_LABELS).map(([key, label]) => (
              <button
                key={key}
                data-testid={`visibility-${key}`}
                onClick={() => toggleSection(key)}
                className={`text-left p-2 border-2 transition-colors text-xs ${
                  visibility[key]
                    ? "border-[#D4AF37] bg-[#D4AF37]/5 text-white"
                    : "border-zinc-800 text-neutral-500 hover:border-zinc-600"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-display">{label}</span>
                  <span>{visibility[key] ? "✓" : "—"}</span>
                </div>
              </button>
            ))}
          </div>
          <button
            data-testid="profile-share-btn"
            onClick={() => setShareOpen(true)}
            className="btn-omni btn-red w-full"
          >
            <Share2 size={14} /> Share My Profile
          </button>
        </>
      )}

      {shareOpen && (
        <ShareProfileModal
          handle={handle}
          name={user?.name}
          onClose={() => setShareOpen(false)}
        />
      )}
    </section>
  );
}
